import { api } from './index';
import type { RequirementFrequency } from './planning/requirements';

const BASE_PATH = '/api/internal/erp_integration';

// The "Integración Odoo" page (ADR 0001, "Visibility"): what the company's ERP
// connection left in Kuido for a coordinator to decide. Kuido's database only;
// what the ERP holds arrives with the pull and the nightly reconcile.

export type SessionList = 'without_line' | 'problems' | 'adoption';

export interface ErpConnectionHealth {
  provider: string;
  active: boolean;
  autoImport: boolean;
  lastPullAt: string | null;
  lastPushAt: string | null;
  lastErrorCode: string | null;
  lastErrorAt: string | null;
  reconciledAt: string | null;
}

export interface ErpCounts {
  toIncorporate: number;
  employees: number;
  unmapped: number;
  withoutLine: number;
  sessionProblems: number;
  adoption: number;
  pendingSessions: number;
}

export interface ErpOverview {
  connection: ErpConnectionHealth;
  counts: ErpCounts;
  // What the connection still lacks to go live (Kuido's team fixes them).
  goLiveMissing: string[];
}

export interface PersonRef {
  id: number;
  name: string;
}

export interface CaregiverRef extends PersonRef {
  career: string;
}

export interface ServiceLine {
  id: number;
  position: number | null;
  productId: number | null;
  productName: string | null;
  quantity: number | null;
  uom: string | null;
  kind: 'service' | 'package' | 'unmapped' | null;
  startsOn: string | null;
  endsOn: string | null;
}

export interface SalesOrderRow {
  id: number;
  reference: string | null;
  patientLabel: string | null;
  startsOn: string | null;
  endsOn: string | null;
  reason: string | null;
  // Why a patient's Sales order is back: what changed in Odoo (quantity,
  // notes) and its lines in no Requirement yet.
  changes: string[];
  addedLines: { career: string | null; productName: string | null }[];
  group: string | null;
  patient: PersonRef | null;
  suggestedPatient: PersonRef | null;
}

export interface RequirementSummary {
  id: number;
  career: string;
  skillId: number | null;
  frequency: RequirementFrequency;
  visitsPerPeriod: number;
  durationMin: number | null;
  startsOn: string;
  endsOn: string | null;
}

export interface ProductRule {
  career: string | null;
  durationMin: number | null;
  blockHours: number | null;
  package: boolean;
}

// What the quantity sold suggests (never saved without the coordinator).
export interface QuantitySuggestion {
  career: string | null;
  durationMin: number | null;
  frequency: RequirementFrequency;
  visitsPerPeriod: number | null;
  source: 'sessions' | 'daily' | null;
}

// The AI reading of the notes for one line, with its verbatim quote.
export interface NotesSuggestion {
  visitsPerPeriod?: number | null;
  frequency?: RequirementFrequency | null;
  weekdays?: string[] | null;
  startTime?: string | null;
  endTime?: string | null;
  durationMin?: number | null;
  evidence?: string | null;
}

// The AI reading of a Package line's notes for one of its careers.
export interface CareerSuggestion extends NotesSuggestion {
  career: string;
}

export interface IncorporationLine extends ServiceLine {
  orderId: number | null;
  // The Requirements the line feeds already: one, or one per career on a Package line.
  requirements: RequirementSummary[];
  // The patient's one Requirement a new line starts as continuing (its career,
  // running up to it, no other line on those days).
  continuesRequirementId: number | null;
  rule: ProductRule | null;
  suggestion: QuantitySuggestion | null;
  ai: NotesSuggestion | null;
  // On a Package line, one reading per career instead of `ai`.
  aiCareers: CareerSuggestion[];
}

export interface IncorporationOrder {
  id: number;
  reference: string | null;
  patientLabel: string | null;
  startsOn: string | null;
  endsOn: string | null;
  notes: string | null;
}

// A patient the lines may go to: since when Kuido sends its Sessions (null
// before its incorporation) and the Requirements a line may continue.
export interface PatientChoice extends PersonRef {
  goLiveOn: string | null;
  requirements: RequirementSummary[];
}

export interface ErpCustomer {
  name: string | null;
  company: boolean;
}

export interface IncorporationScreen {
  mode: 'incorporate' | 'confirm';
  orders: IncorporationOrder[];
  lines: IncorporationLine[];
  patient: (PatientChoice & { linked: boolean }) | null;
  // A new patient's go-live date (from today) and a Linked one's: a patient
  // Kuido already cares for may go back to the Sales orders' first day, within
  // this month.
  defaultGoLiveOn: string;
  defaultLinkGoLiveOn: string;
  // Who pays the Sales orders in Odoo: a new patient's representative.
  customer: ErpCustomer | null;
  // What Odoo's name lacks comes from the AI's reading of the notes (fromNotes).
  name: {
    firstName: string | null; lastName: string | null; dni: string | null;
    fromNotes: ('first_name' | 'last_name' | 'dni')[];
  };
}

// A Service line of an incorporated patient and the Requirements it feeds.
export interface FeedingLine extends ServiceLine {
  // 'sale' while sold; 'removed' from its Sales order; else the order's own state.
  state: string | null;
  dismissed: boolean;
  requirements: RequirementSummary[];
}

export interface PatientSalesOrder {
  id: number;
  reference: string | null;
  state: string | null;
  startsOn: string | null;
  endsOn: string | null;
  lines: FeedingLine[];
}

// An incorporated patient: since when Kuido sends its Sessions (movable until
// that day arrives) and how its Sales orders feed its Requirements.
export interface IncorporatedPatient extends PersonRef {
  goLiveOn: string;
  goLiveMovable: boolean;
  orders: PatientSalesOrder[];
}

// One Requirement the coordinator confirms: an existing one the lines continue,
// or the values of a new one.
export interface IncorporationRow {
  lineIds: number[];
  requirementId?: number;
  career?: string;
  skillId?: number | null;
  durationMin?: number | null;
  frequency?: RequirementFrequency;
  visitsPerPeriod?: number;
}

// A Requirement a Package line stops feeding: a career taken off it.
export interface ReleasedRequirement {
  lineId: number;
  requirementId: number;
}

export interface IncorporationPayload {
  orderIds: number[];
  rows: IncorporationRow[];
  released: ReleasedRequirement[];
  dismissedLineIds: number[];
  goLiveOn?: string;
  patientId?: number;
  // A new patient, with its main address located on the map.
  patient?: {
    firstName?: string; lastName?: string; dni?: string; address?: string;
    latitude?: string | null; longitude?: string | null;
  };
  groupIds?: number[];
}

export interface ShiftRef {
  id: number;
  startsAt: string;
  endsAt: string;
  suspended: boolean;
  patient: PersonRef | null;
  caregiver: CaregiverRef | null;
}

export interface CandidateLine extends ServiceLine {
  reference: string | null;
  requirement: RequirementSummary | null;
}

export interface PayCheck {
  expected: { grossCost: number; withholding: number };
  erp: { grossCost: number; withholding: number };
}

export interface SessionRow {
  id: number;
  remoteId: number | null;
  status: 'pending' | 'synced' | 'failed';
  code: string | null;
  // A done Shift kept on a line that no longer covers its date.
  conflict?: string | null;
  pickedLineId: number | null;
  shift: ShiftRef | null;
  candidates?: CandidateLine[];
  candidateSessionIds?: number[];
  drift?: string[] | null;
  driftOn?: string | null;
  payCheck?: PayCheck | null;
  lineCheck?: { expected: number; erp: number | null } | null;
  attempts?: number;
}

export interface UnclaimedSession {
  remoteId: number;
  employeeId: number;
  taskId: number;
  quantity: number;
  pay: number;
  date: string;
  state: string;
  caregiver: CaregiverRef | null;
  patient: PersonRef | null;
}

export interface SessionsPage {
  rows: SessionRow[];
  total?: number;
  unclaimed?: UnclaimedSession[];
  unclaimedTotal?: number;
}

export interface EmployeeReview {
  id: number;
  remoteId: number;
  name: string | null;
  reason: string | null;
  attributes: string[];
  caregiver: CaregiverRef | null;
}

export interface ErpEmployees {
  reviews: EmployeeReview[];
  unready: CaregiverRef[];
}

export interface ErpUnmapped {
  products: { productId: number; productName: string | null; lines: number; salesOrders: number }[];
  jobs: { jobId: number | null; jobName: string | null; employees: number }[];
}

export interface ErpTotalsRow {
  employeeId: number;
  caregiver: CaregiverRef | null;
  kuido: number;
  erp: number | null;
  difference: number | null;
  shifts: number;
  unsent: number;
}

export interface ErpTotals {
  month: string;
  months: string[];
  reconciledAt: string | null;
  rows: ErpTotalsRow[];
}

export const erpIntegrationApi = {
  overview(): Promise<ErpOverview> {
    return api({ method: 'get', url: `${BASE_PATH}/overview` }).then(r => r.data.erpOverview);
  },
  salesOrders(): Promise<SalesOrderRow[]> {
    return api({ method: 'get', url: `${BASE_PATH}/sales_orders` })
      .then(r => r.data.salesOrders);
  },
  incorporation(orderId: number): Promise<IncorporationScreen> {
    return api({ method: 'get', url: `${BASE_PATH}/sales_orders/${orderId}` })
      .then(r => r.data.incorporation);
  },
  patient(patientId: number): Promise<PatientChoice> {
    return api({ method: 'get', url: `${BASE_PATH}/patients/${patientId}` })
      .then(r => r.data.patient);
  },
  incorporatedPatients(): Promise<IncorporatedPatient[]> {
    return api({ method: 'get', url: `${BASE_PATH}/patients` }).then(r => r.data.patients);
  },
  moveGoLive(patientId: number, goLiveOn: string): Promise<void> {
    return api({ method: 'patch', url: `${BASE_PATH}/patients/${patientId}`, data: { goLiveOn } })
      .then(() => undefined);
  },
  dismiss(orderId: number): Promise<void> {
    return api({ method: 'post', url: `${BASE_PATH}/sales_orders/${orderId}/dismiss` })
      .then(() => undefined);
  },
  incorporate(payload: IncorporationPayload): Promise<{ id: number }> {
    return api({ method: 'post', url: `${BASE_PATH}/incorporations`, data: payload })
      .then(r => r.data.patient);
  },
  employees(): Promise<ErpEmployees> {
    return api({ method: 'get', url: `${BASE_PATH}/employees` }).then(r => r.data.employees);
  },
  unmapped(): Promise<ErpUnmapped> {
    return api({ method: 'get', url: `${BASE_PATH}/unmapped` }).then(r => r.data.unmapped);
  },
  sessions(list: SessionList): Promise<SessionsPage> {
    return api({ method: 'get', url: `${BASE_PATH}/sessions`, params: { list } })
      .then(r => r.data.sessions);
  },
  pickLine(sessionId: number, lineId: number): Promise<void> {
    return api({
      method: 'post', url: `${BASE_PATH}/sessions/${sessionId}/pick_line`, data: { lineId },
    }).then(() => undefined);
  },
  retry(sessionId: number): Promise<void> {
    return api({ method: 'post', url: `${BASE_PATH}/sessions/${sessionId}/retry` })
      .then(() => undefined);
  },
  totals(month?: string): Promise<ErpTotals> {
    return api({ method: 'get', url: `${BASE_PATH}/totals`, params: { month } })
      .then(r => r.data.totals);
  },
  sync(): Promise<void> {
    return api({ method: 'post', url: `${BASE_PATH}/sync` }).then(() => undefined);
  },
};
